import Box, { boxClasses } from '@mui/material/Box';
import { SxProps, Theme, useTheme } from '@mui/material/styles';
import TableRow, { TableRowProps } from '@mui/material/TableRow';
import { OrderState } from '@treadinc/horizon-api-spec';
import { t } from 'i18next';
import { observer } from 'mobx-react-lite';
import {
  ComponentProps,
  MouseEvent,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

import Checkbox from '~components/Checkbox/Checkbox';
import DepartmentBadge from '~components/Common/DepartmentBadge';
import { CopyToClipboard } from '~components/CopyToClipboard/CopyToClipboard';
import actionsColumn from '~components/DataTable/actionsColumn';
import DataTable from '~components/DataTable/DataTable';
import { DataTableColumn } from '~components/DataTable/dataTableColumn';
import HighlightedText from '~components/HighlightedText/HighlightedText';
import {
  OrderBulkAssignAndMessagesButtons,
  OrderEditAndMenuButtons,
  OrderIndicatorBadges,
  OrderStatusControls,
} from '~components/Order/ordersDispatchColumns';
import OrderCardAttachmentPreview from '~components/OrderCard/OrderCardAttachmentPreview';
import OrderRateCardIndicator from '~components/OrderCard/OrderRateCardIndicator';
import ServiceClassChip from '~components/OrderCard/ServiceClassChip';
import useOrderCustomerRate from '~components/OrderCard/useOrderCustomerRate';
import { useOrderSearchHighlightTerm } from '~components/OrderSearchHighlightContext/OrderSearchHighlightContext';
import OrderSearchMatchBadge from '~components/OrderSearchMatchBadge/OrderSearchMatchBadge';
import useDispatchNumberLabel from '~hooks/useDispatchNumberLabel';
import { Order } from '~hooks/useOrders/models';
import useEnsureOrderJobs from '~hooks/useOrders/useEnsureOrderJobs';
import useOrderMenuHandlers from '~hooks/useOrders/useOrderMenuHandlers';
import { useStores } from '~store';
import { isOrderExpanded } from '~store/OrdersDispatchStore';
import { Nullable } from '~types/Nullable';
import {
  extractWaypointData,
  formatContactText,
  formatOrderStartTime,
  getOrderCheckboxState,
  getOrderDeliveredLoads,
  getOrderNotes,
  getOrderTicketsSummary,
  getOrderTrucksProgress,
  makeOrderedMaterialText,
  makeOrderNameWithDropOffFallback,
  makeOrderSenderDisplay,
  openOrderMessagingTab,
} from '~utils/orders';

import { ORDER_DISPATCH_CARDS_CONTAINER_GUTTER_SIZE } from './constants';
import { getJobCycleTimeText } from './dispatchExportColumns';
import OrderDispatchCardExpandedBlock from './OrderDispatchCardExpandedBlock';
import { OrdersListProps } from './OrdersList';
import OrdersListFooter from './OrdersListFooter';
import trackOrderDispatchTabViewed, {
  OrderDispatchCardExpandedBlockTab,
} from './trackOrderDispatchTabViewed';
import useIsOrderAwaitingResponse from './useIsOrderAwaitingResponse';
import useOrderDispatchHiddenFields from './useOrderDispatchHiddenFields';
import useOrderSearchMatches from './useOrderSearchMatches';

const CONTACT_LIST_COLUMNS = [
  ['foremen', 'form_fields.foreman'],
  ['collaborators', 'form_fields.collaborator'],
] as const;

const CONTACT_COLUMNS = [
  ['salesContact', 'form_fields.contact_salesman'],
  ['foremanContact', 'form_fields.contact_foreman'],
  ['collaboratorContact', 'form_fields.contact_collaborator'],
  ['supervisorContact', 'form_fields.contact_supervisor'],
] as const;

const NOTES_COLUMNS = [
  ['jobNotes', 'dispatch.order.job_notes'],
  ['orderNotes', 'dispatch.order.order_notes'],
  ['internalNotes', 'form_fields.internal_notes'],
] as const;

const customerTextSx = (theme: Theme) => ({
  color: theme.brandV2.colors.treadGreen,
  fontWeight: 600,
});

const truncatedTextSx = { overflow: 'hidden', textOverflow: 'ellipsis' };

interface HighlightedCellProps {
  onClick?: (event: MouseEvent) => void;
  sx?: SxProps<Theme>;
  text?: Nullable<string>;
}

function HighlightedCell({ onClick, sx, text }: HighlightedCellProps) {
  const searchTerm = useOrderSearchHighlightTerm();

  if (!text) {
    return null;
  }

  return (
    <Box
      component="span"
      onClick={onClick}
      sx={[truncatedTextSx, ...(Array.isArray(sx) ? sx : [sx])]}
    >
      <HighlightedText searchTerm={searchTerm} text={text} />
    </Box>
  );
}

interface ContactCellProps {
  contacts: Array<{ name: string; phone?: Nullable<string> }>;
}

function ContactCell({ contacts }: ContactCellProps) {
  const copyFirstPhone = (event: MouseEvent) => {
    event.stopPropagation();
    navigator.clipboard.writeText(contacts[0]?.phone ?? '');
  };

  return (
    <HighlightedCell
      onClick={copyFirstPhone}
      text={contacts.map(({ name, phone }) => formatContactText(name, phone)).join(', ')}
    />
  );
}

const OrderIdCell = observer(({ order }: { order: Order }) => {
  const searchMatches = useOrderSearchMatches(order);

  return (
    // The row can't grow, so the match badge stays on the ID's line instead of wrapping.
    <Box
      sx={{
        alignItems: 'center',
        display: 'flex',
        gap: 1,
        [`& .${boxClasses.root}`]: { flexWrap: 'nowrap' },
      }}
    >
      <CopyToClipboard
        alertMessageDuration={1000}
        hideTextWhileCopying
        textToCopy={order.orderId}
      >
        <HighlightedCell text={order.orderId} />
      </CopyToClipboard>
      {searchMatches.length > 0 && (
        <OrderSearchMatchBadge searchMatches={searchMatches} />
      )}
    </Box>
  );
});

function CustomerRateCell({ order }: { order: Order }) {
  const { customerRateText, shouldShowRateCardIndicator } = useOrderCustomerRate(order);

  return (
    <>
      {customerRateText}
      {shouldShowRateCardIndicator && <OrderRateCardIndicator order={order} />}
    </>
  );
}

interface TrucksProgressCellProps {
  order: Order;
  part: 'acceptedText' | 'total';
}

const TrucksProgressCell = observer(({ order, part }: TrucksProgressCellProps) => {
  const { ordersDispatchStore } = useStores();

  return getOrderTrucksProgress(
    order,
    ordersDispatchStore.ordersWaitingForTrucksCountToMatchCountInSummary.has(order.id),
  )[part];
});

interface OrderCheckboxCellProps {
  checkedJobIds?: string[];
  onChange: () => void;
  orderId: string;
}

const OrderCheckboxCell = observer(
  ({ checkedJobIds, onChange, orderId }: OrderCheckboxCellProps) => {
    const { ordersDispatchStore } = useStores();

    return (
      <Checkbox
        {...getOrderCheckboxState({
          checkedJobIds,
          isLoadingOrderJobs: ordersDispatchStore.isLoadingOrderJobs,
          orderId,
          orderJobs: ordersDispatchStore.orderJobs,
          readOnly: false,
        })}
        onChange={onChange}
      />
    );
  },
);

interface OrderStatusCellProps
  extends
    ComponentProps<typeof OrderStatusControls>,
    ComponentProps<typeof OrderBulkAssignAndMessagesButtons> {}

function OrderStatusCell({
  onBulkAssignMultiple,
  onOpenMessagingTab,
  order,
  ...statusControlsProps
}: OrderStatusCellProps) {
  return (
    <Box
      onClick={(event) => event.stopPropagation()}
      sx={{ alignItems: 'center', display: 'flex', gap: 1 }}
    >
      <OrderIndicatorBadges order={order} />
      <OrderStatusControls {...statusControlsProps} order={order} />
      <Box sx={{ alignItems: 'center', display: 'flex' }}>
        <OrderBulkAssignAndMessagesButtons
          onBulkAssignMultiple={onBulkAssignMultiple}
          onOpenMessagingTab={onOpenMessagingTab}
          order={order}
        />
      </Box>
    </Box>
  );
}

type OrderActionsCellProps = Omit<
  ComponentProps<typeof OrderEditAndMenuButtons>,
  'onDownloadJobCardPdfs' | 'onViewLoadDigest'
>;

const OrderActionsCell = observer(
  ({
    onDownloadFiles,
    onEmailLoadDigest,
    order,
    ...buttonsProps
  }: OrderActionsCellProps) => {
    const menuHandlers = useOrderMenuHandlers({
      onDownloadFiles,
      onEmailLoadDigest,
      order,
    });

    return (
      <Box
        onClick={(event) => event.stopPropagation()}
        sx={{ alignItems: 'center', display: 'flex' }}
      >
        <OrderEditAndMenuButtons {...buttonsProps} {...menuHandlers} order={order} />
      </Box>
    );
  },
);

interface OrderDataColumnsOptions {
  dispatchNumberLabel: (fallbackKey: string) => string;
  hideEquipmentType: boolean;
  hideMaterials: boolean;
  hidePickupAndDropoff: boolean;
  hideStartTime: boolean;
}

function makeOrderDataColumns({
  dispatchNumberLabel,
  hideEquipmentType,
  hideMaterials,
  hidePickupAndDropoff,
  hideStartTime,
}: OrderDataColumnsOptions): Array<DataTableColumn<Order>> {
  return [
    {
      field: 'orderId',
      headerName: t('form_fields.order_id'),
      // Fits the "Copied!" text that briefly replaces the ID, so the column doesn't jump.
      minWidth: 90,
      renderCell: (order) => <OrderIdCell order={order} />,
    },
    {
      field: 'dispatchNumber',
      headerName: dispatchNumberLabel('export.columns.dispatch_number'),
      renderCell: (order) => <HighlightedCell text={order.dispatchNumber?.name} />,
    },
    {
      defaultHidden: true,
      field: 'orderName',
      headerName: t('order.form.order_name'),
      renderCell: (order) => (
        <HighlightedCell text={makeOrderNameWithDropOffFallback(order)} />
      ),
    },
    {
      defaultHidden: true,
      field: 'attachments',
      headerName: t('form_fields.attachments'),
      renderCell: (order) => {
        const attachmentCount = order.orderSummary?.fileAttachmentsCount ?? 0;

        return attachmentCount > 0 ? (
          <OrderCardAttachmentPreview
            attachmentCount={attachmentCount}
            orderId={order.id}
            orderNumber={order.orderId}
          />
        ) : null;
      },
    },
    {
      field: 'customer',
      headerName: t('form_fields.customer'),
      renderCell: (order) => (
        <HighlightedCell
          sx={customerTextSx}
          text={makeOrderSenderDisplay(order) || order.account?.name}
        />
      ),
    },
    {
      defaultHidden: true,
      field: 'projectAccount',
      headerName: t('common.project_account'),
      renderCell: (order) => (
        <HighlightedCell sx={customerTextSx} text={order.account?.name} />
      ),
    },
    {
      defaultHidden: true,
      field: 'chargeToAccount',
      headerName: t('dispatch.order.charge_to_account'),
      renderCell: (order) => (
        <Box component="span" sx={[customerTextSx, truncatedTextSx]}>
          {order.chargeToAccount}
        </Box>
      ),
    },
    {
      defaultHidden: true,
      field: 'poJobNumber',
      headerName: t('order.form.order_po'),
      valueGetter: (order) => order.poJobNumber,
    },
    {
      defaultHidden: true,
      field: 'zone',
      headerName: t('order.form.order_zone'),
      valueGetter: (order) => order.zone,
    },
    {
      defaultHidden: true,
      field: 'externalId',
      headerName: t('form_fields.external_id'),
      renderCell: (order) => <HighlightedCell text={order.externalId} />,
    },
    {
      defaultHidden: true,
      field: 'serviceClass',
      headerName: t('form_fields.service_class'),
      renderCell: (order) =>
        order.serviceClass?.name ? (
          <ServiceClassChip serviceClass={order.serviceClass} />
        ) : null,
    },
    {
      defaultHidden: true,
      field: 'customerRate',
      headerName: t('dispatch.job.customer_rate'),
      renderCell: (order) => <CustomerRateCell order={order} />,
    },
    {
      field: 'project',
      headerName: t('form_fields.project'),
      renderCell: (order) => <HighlightedCell text={order.projectName} />,
    },
    {
      defaultHidden: true,
      field: 'phaseCode',
      headerName: t('form_fields.phase_code'),
      valueGetter: (order) => order.phases?.find((phase) => phase.primary)?.code,
    },
    {
      defaultHidden: true,
      field: 'projectExternalId',
      headerName: t('dispatch.dispatch_v3.project_external_id'),
      renderCell: (order) => <HighlightedCell text={order.project?.externalId} />,
    },
    ...(hideStartTime
      ? []
      : [
          {
            field: 'loadAt',
            headerName: t('form_fields.start_time'),
            valueGetter: formatOrderStartTime,
          },
        ]),
    ...(hideEquipmentType
      ? []
      : [
          {
            field: 'truckType',
            headerName: t('form_fields.truck_type'),
            valueGetter: (order: Order) => order.equipmentType?.name,
          },
        ]),
    {
      field: 'trucks',
      headerName: t('form_fields.trucks'),
      renderCell: (order) => <TrucksProgressCell order={order} part="total" />,
    },
    {
      field: 'accepted',
      headerName: t('dispatch.summary_counts.accepted'),
      renderCell: (order) => <TrucksProgressCell order={order} part="acceptedText" />,
    },
    {
      field: 'loads',
      headerName: t('common.loads'),
      valueGetter: (order) => order.orderSummary?.loadsCount ?? 0,
    },
    {
      field: 'delivered',
      headerName: t('dispatch.dispatch_v2.delivered'),
      valueGetter: (order) => {
        const { deliveredLoadsCount, deliveredQuantityText, plannedText } =
          getOrderDeliveredLoads(order);
        const deliveredLoadsText = `${deliveredLoadsCount} / ${plannedText}`;

        return deliveredQuantityText
          ? `${deliveredLoadsText} • ${deliveredQuantityText}`
          : deliveredLoadsText;
      },
    },
    {
      field: 'tickets',
      headerName: t('dispatch.dispatch_v2.tickets'),
      // Column floors measure only cell content; this fits the "Tickets" header.
      minWidth: 80,
      valueGetter: (order) => {
        const { ticketedQuantityText, ticketsCount } = getOrderTicketsSummary(order);

        return ticketedQuantityText
          ? `${ticketedQuantityText} • ${ticketsCount}`
          : ticketsCount;
      },
    },
    ...(hideMaterials
      ? []
      : [
          {
            field: 'material',
            headerName: t('form_fields.material'),
            valueGetter: (order: Order) => order.material?.name,
          },
        ]),
    {
      field: 'materialOrdered',
      headerName: t('dispatch.dispatch_v3.delivered_quantities_widget.label'),
      valueGetter: makeOrderedMaterialText,
    },
    ...(hidePickupAndDropoff
      ? []
      : [
          {
            field: 'pickup',
            headerName: t('form_fields.pickup'),
            renderCell: (order: Order) => (
              <HighlightedCell
                text={extractWaypointData(order.pickUpWayPoints?.[0])?.name}
              />
            ),
          },
          {
            field: 'dropoff',
            headerName: t('form_fields.dropoff'),
            renderCell: (order: Order) => (
              <HighlightedCell
                text={extractWaypointData(order.dropOffWayPoints?.[0])?.name}
              />
            ),
          },
        ]),
    {
      defaultHidden: true,
      field: 'cycle',
      headerName: t('dispatch.dispatch_v2.cycle_time'),
      valueGetter: getJobCycleTimeText,
    },
    {
      defaultHidden: true,
      field: 'requester',
      headerName: t('form_fields.requester'),
      valueGetter: (order) => order.requester?.fullName,
    },
    ...CONTACT_LIST_COLUMNS.map(([field, labelKey]) => ({
      defaultHidden: true,
      field,
      headerName: t(labelKey),
      renderCell: (order: Order) => (
        <ContactCell
          contacts={order[field].map(({ fullName, phone }) => ({
            name: fullName,
            phone,
          }))}
        />
      ),
    })),
    ...CONTACT_COLUMNS.map(([field, labelKey]) => ({
      defaultHidden: true,
      field,
      headerName: `${t(labelKey)} ${t('common.contact')}`,
      renderCell: (order: Order) => {
        const contact = order[field];

        return <ContactCell contacts={contact ? [contact] : []} />;
      },
    })),
    ...NOTES_COLUMNS.map(([field, labelKey]) => ({
      defaultHidden: true,
      field,
      headerName: t(labelKey),
      valueGetter: (order: Order) => getOrderNotes(order, field),
    })),
    {
      field: 'department',
      headerName: t('form_fields.department'),
      renderCell: (order) => (
        <DepartmentBadge department={order.department ?? undefined} />
      ),
    },
  ];
}

interface OrdersGridDetailPanelProps {
  checkedJobs?: string[];
  isMessagingTabRequested: boolean;
  onCheckedStateChange: (orderId: string, jobId?: string) => void;
  onCreateJobFromOrderClick: (orderId: string) => void;
  onMessagingTabRequestHandled: () => void;
  order: Order;
}

// About an expanded order with a few jobs.
const DETAIL_PANEL_PLACEHOLDER_HEIGHT_PX = 260;

// Expanding every row first mounts as many panels as fit at row height, until the
// virtualizer measures them. Showing a panel-height placeholder for two frames lets it
// drop the off-screen ones before their content renders and tracks a tab view.
function OrdersGridDetailPanel(props: OrdersGridDetailPanelProps) {
  const [isContentShown, setIsContentShown] = useState(false);

  useEffect(() => {
    let frame = requestAnimationFrame(() => {
      frame = requestAnimationFrame(() => setIsContentShown(true));
    });

    return () => cancelAnimationFrame(frame);
  }, []);

  return isContentShown ? (
    <OrdersGridDetailPanelContent {...props} />
  ) : (
    <Box sx={{ height: DETAIL_PANEL_PLACEHOLDER_HEIGHT_PX }} />
  );
}

const OrdersGridDetailPanelContent = observer(
  ({
    checkedJobs,
    isMessagingTabRequested,
    onCheckedStateChange,
    onCreateJobFromOrderClick,
    onMessagingTabRequestHandled,
    order,
  }: OrdersGridDetailPanelProps) => {
    const handleCheckedJobStateChange = useCallback(
      (jobId: string) => onCheckedStateChange(order.id, jobId),
      [onCheckedStateChange, order.id],
    );

    const handleCreateJobFromOrder = useCallback(
      () => onCreateJobFromOrderClick(order.id),
      [onCreateJobFromOrderClick, order.id],
    );

    return (
      <OrderDispatchCardExpandedBlock
        checkedJobs={checkedJobs}
        isMessagingTabRequested={isMessagingTabRequested}
        onCheckedJobStateChange={handleCheckedJobStateChange}
        onCreateJobFromOrder={handleCreateJobFromOrder}
        onMessagingTabRequestHandled={onMessagingTabRequestHandled}
        order={order}
        presentation="grid"
        readOnly={false}
      />
    );
  },
);

// Rows load their jobs even while collapsed: the checkbox and the External accent need them.
const OrdersGridRow = observer(
  ({ row: order, ...props }: TableRowProps & { row: Order }) => {
    useEnsureOrderJobs(order.id);

    return (
      <TableRow
        {...props}
        data-ui={`page-dispatch__order-dispatch__order-${order.orderId}__button`}
      />
    );
  },
);

interface OrdersGridProps extends OrdersListProps {
  columnsMenuContainer?: HTMLElement | null;
}

const OrdersGrid = observer((props: OrdersGridProps) => {
  const {
    checkedOrdersAndJobs,
    columnsMenuContainer,
    onExpandedStateChange,
    onLoadMore,
    onShowAllDates,
    selectedDateRangeLabel,
  } = props;
  const theme = useTheme();
  const { ordersDispatchStore } = useStores();
  const { singular: dispatchNumberLabel } = useDispatchNumberLabel();
  const [messagingRequestedOrderId, setMessagingRequestedOrderId] = useState<string>();
  const { orderCardsExpandedState, ordersSnapshot } = ordersDispatchStore;

  // The handlers OrdersView passes are new on every render; reading them through a ref
  // keeps the columns and panels from rebuilding with them.
  const latestPropsRef = useRef(props);
  latestPropsRef.current = props;

  const { hideEquipmentType, hideMaterials, hidePickupAndDropoff, hideStartTime } =
    useOrderDispatchHiddenFields();

  const expandedOrderIds = useMemo(
    () =>
      ordersSnapshot
        .filter((order) => isOrderExpanded(orderCardsExpandedState, order.id))
        .map((order) => order.id),
    [orderCardsExpandedState, ordersSnapshot],
  );

  const trackTabViewedIfCollapsed = useCallback(
    (order: Order, tab: OrderDispatchCardExpandedBlockTab) => {
      if (!ordersDispatchStore.isOrderCardExpanded(order.id)) {
        trackOrderDispatchTabViewed(order, tab, 'expanded');
      }
    },
    [ordersDispatchStore],
  );

  const toggleOrderExpandedFromClick = useCallback(
    (order: Order) => {
      trackTabViewedIfCollapsed(order, OrderDispatchCardExpandedBlockTab.JOBS);
      onExpandedStateChange(order.id);
    },
    [onExpandedStateChange, trackTabViewedIfCollapsed],
  );

  const handleExpandedRowIdsChange = useCallback(
    (nextExpandedOrderIds: string[]) => {
      ordersSnapshot
        .filter(
          (order) =>
            expandedOrderIds.includes(order.id) !==
            nextExpandedOrderIds.includes(order.id),
        )
        .forEach(toggleOrderExpandedFromClick);
    },
    [expandedOrderIds, ordersSnapshot, toggleOrderExpandedFromClick],
  );

  const handleCheckedStateChange = useCallback(
    (orderId: string, jobId?: string) =>
      latestPropsRef.current.onCheckedStateChange(orderId, jobId),
    [],
  );

  const handleCreateJobFromOrderClick = useCallback(
    (orderId: string) => latestPropsRef.current.onCreateJobFromOrderClick(orderId),
    [],
  );

  const clearMessagingTabRequest = useCallback(
    () => setMessagingRequestedOrderId(undefined),
    [],
  );

  const openMessagingTab = useCallback(
    (order: Order) => {
      openOrderMessagingTab({
        isExpanded: ordersDispatchStore.isOrderCardExpanded(order.id),
        onMessagingTabRequest: () => {
          trackTabViewedIfCollapsed(order, OrderDispatchCardExpandedBlockTab.MESSAGING);
          setMessagingRequestedOrderId(order.id);
        },
        onToggleExpanded: () => latestPropsRef.current.onExpandedStateChange(order.id),
      });
    },
    [ordersDispatchStore, trackTabViewedIfCollapsed],
  );

  const cancelOrder = useCallback(
    (order: Order) =>
      latestPropsRef.current.onOrderStateChange(order.id, OrderState.CANCELED),
    [],
  );

  const columns = useMemo(
    (): Array<DataTableColumn<Order>> => [
      {
        field: 'select',
        headerName: t('data_table.select_row'),
        hideable: false,
        isHeaderNameHidden: true,
        pin: 'left',
        resizable: false,
        renderCell: (order) => (
          <OrderCheckboxCell
            checkedJobIds={checkedOrdersAndJobs[order.id]}
            onChange={() => latestPropsRef.current.onCheckedStateChange(order.id)}
            orderId={order.id}
          />
        ),
      },
      ...makeOrderDataColumns({
        dispatchNumberLabel,
        hideEquipmentType,
        hideMaterials,
        hidePickupAndDropoff,
        hideStartTime,
      }),
      {
        field: 'status',
        headerName: t('form_fields.status'),
        hideable: false,
        resizable: false,
        renderCell: (order) => (
          <OrderStatusCell
            onAccept={() => latestPropsRef.current.onAcceptOrder(order.id)}
            onBulkAssignMultiple={() =>
              latestPropsRef.current.onBulkAssignMultipleClick?.(order)
            }
            onCancel={() => cancelOrder(order)}
            onOpenMessagingTab={() => openMessagingTab(order)}
            onOrderStateChange={(nextOrderState) =>
              latestPropsRef.current.onOrderStateChange(order.id, nextOrderState)
            }
            onReject={() => latestPropsRef.current.onRejectOrder(order)}
            order={order}
          />
        ),
      },
      actionsColumn((order) => (
        <OrderActionsCell
          onCancel={() => cancelOrder(order)}
          onCloneOrder={() => latestPropsRef.current.onCloneOrderClick(order)}
          onCopyAndEdit={() => latestPropsRef.current.onCopyAndEditClick(order)}
          onDownloadFiles={
            latestPropsRef.current.onDownloadFilesClick
              ? () => latestPropsRef.current.onDownloadFilesClick?.(order)
              : undefined
          }
          onEdit={() => latestPropsRef.current.onEditClick(order)}
          onOpenMessagingTab={() => openMessagingTab(order)}
          onTextAllDrivers={() => latestPropsRef.current.onTextAllDriversClick(order)}
          order={order}
        />
      )),
    ],
    [
      cancelOrder,
      checkedOrdersAndJobs,
      dispatchNumberLabel,
      hideEquipmentType,
      hideMaterials,
      hidePickupAndDropoff,
      hideStartTime,
      openMessagingTab,
    ],
  );

  const renderDetailPanel = useCallback(
    (order: Order) => (
      <OrdersGridDetailPanel
        checkedJobs={checkedOrdersAndJobs[order.id]}
        isMessagingTabRequested={messagingRequestedOrderId === order.id}
        onCheckedStateChange={handleCheckedStateChange}
        onCreateJobFromOrderClick={handleCreateJobFromOrderClick}
        onMessagingTabRequestHandled={clearMessagingTabRequest}
        order={order}
      />
    ),
    [
      checkedOrdersAndJobs,
      clearMessagingTabRequest,
      handleCheckedStateChange,
      handleCreateJobFromOrderClick,
      messagingRequestedOrderId,
    ],
  );

  // Resolved during this observer's render, so loading jobs re-renders the accents.
  const isOrderAwaitingResponse = useIsOrderAwaitingResponse();
  const orderIdsAwaitingResponse = new Set(
    ordersSnapshot
      .filter((order) => isOrderAwaitingResponse(order.id))
      .map((order) => order.id),
  );
  const getRowAccentColor = (order: Order, isExpanded: boolean) => {
    if (orderIdsAwaitingResponse.has(order.id)) {
      return theme.brandV2.colors.treadOrange;
    }

    return isExpanded ? theme.brandV2.colors.treadYellow : undefined;
  };
  const getRowColors = (order: Order, isExpanded: boolean) => ({
    accent: getRowAccentColor(order, isExpanded),
    surface: isExpanded ? theme.semantic.surfaceExpanded : undefined,
  });

  const footer = (
    <OrdersListFooter
      onShowAllDates={onShowAllDates}
      selectedDateRangeLabel={selectedDateRangeLabel}
    />
  );

  return ordersSnapshot.length > 0 ? (
    <Box
      sx={{
        boxSizing: 'border-box',
        height: '100%',
        p: ORDER_DISPATCH_CARDS_CONTAINER_GUTTER_SIZE,
      }}
    >
      <DataTable
        columns={columns}
        columnsMenuContainer={columnsMenuContainer}
        density="compact"
        expandedRowIds={expandedOrderIds}
        footer={footer}
        getRowColors={getRowColors}
        hideToolbar
        id="dispatch-orders-grid"
        isVirtualized
        onEndReached={onLoadMore}
        onExpandedRowIdsChange={handleExpandedRowIdsChange}
        onRowClick={toggleOrderExpandedFromClick}
        renderDetailPanel={renderDetailPanel}
        rowComponent={OrdersGridRow}
        rows={ordersSnapshot}
        surfaceColor={theme.semantic.surfaceContainer}
        sx={{ height: '100%' }}
      />
    </Box>
  ) : (
    footer
  );
});

export default OrdersGrid;
